import React, { useEffect, useState, useMemo, useCallback } from "react";
import { icons } from "../../../Assets/Icons/icons";
import { createPortal } from "react-dom";
import AddSubCategoryModal from "./AddSubCategoryModal";
import AddOneButton from "../../common/AddOneButton";
import DeleteAlertModal from "../../common/DeleteAlertModal";
import SubCategoryDetailsModal from "./SubCategoryDetailsModal";
import axios from "axios";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";

const AdminSubCategorySkeleton = ({ count = 8 }) => {
  return (
    <div className="my-6 min-h-[250px] grid xl:grid-cols-2 md:grid-cols-2 grid-cols-1 gap-6">
      {
        Array(count)
          .fill("")
          .map((_, i) => (
            <div
              key={i}
              className="flex bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200 animate-pulse"
            >
              {/* Skeleton Image */}
              <div className="w-1/2 min-w-[230px] h-auto bg-gray-200"></div>

              {/* Skeleton Text Section */}
              <div className="flex-1 p-5 flex flex-col justify-between">
                <div>
                  <div className="h-5 bg-gray-200 rounded w-2/3 mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mt-4"></div>
                </div>

                {/* Skeleton Buttons */}
                <div className="flex gap-3 mt-5">
                  <div className="flex-1 h-9 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1 h-9 bg-gray-200 rounded-lg"></div>
                  <div className="flex-1 h-9 bg-gray-200 rounded-lg"></div>
                </div>
              </div>
            </div>
          ))
      }
    </div>
  )
}

const AdminSubCategoryHome = ({
  hideHeader = false,
  selectedCategoryId = "all",
  onClearCategoryFilter,
  onSubCategoryCountChange,
}) => {
  const [isSubAddModal, setIsSubAddModal] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [viewSubCategory, setViewSubCategory] = useState(null);
  const [subCategoryData, setSubCategoryData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(selectedCategoryId || "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [subCategoryToDelete, setSubCategoryToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [entriesPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);

  // Sync category filter if parent changes selectedCategoryId
  useEffect(() => {
    if (selectedCategoryId) {
      setCategoryFilter(selectedCategoryId);
      setCurrentPage(1);
    }
  }, [selectedCategoryId]);

  // Fetch all parent categories for filter dropdown
  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
        );
        setCategories(res.data || []);
      } catch (err) {
        console.error("Error fetching categories for dropdown:", err);
      }
    };
    fetchCats();
  }, []);

  // Helper function to get English name from multilingual object
  const getEnglishName = (item) => {
    if (!item || !item.name) return "";
    
    if (typeof item.name === 'string') {
      return item.name;
    }
    
    if (typeof item.name === 'object') {
      return item.name.en || item.name.english || 
             Object.values(item.name)[0] || 
             "Unnamed Subcategory";
    }
    
    return "Subcategory";
  };

  // Helper function to get English description
  const getEnglishDescription = (item) => {
    if (!item || !item.description) return "No description available";
    
    if (typeof item.description === 'string') {
      return item.description;
    }
    
    if (typeof item.description === 'object') {
      return item.description.en || item.description.english || 
             Object.values(item.description)[0] || 
             "";
    }
    
    return "";
  };

  const fetchSubCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-categories/get-all-sub-categories`
      );
      const list = res.data || [];
      setSubCategoryData(list);
      if (onSubCategoryCountChange) onSubCategoryCountChange(list.length);
      setError(null);
    } catch (err) {
      console.error("Error fetching sub-categories:", err);
      setError("Failed to load sub-categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [onSubCategoryCountChange]);

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  const filteredSubCategories = useMemo(() => {
    return subCategoryData.filter((sub) => {
      const matchesSearch = getEnglishName(sub).toLowerCase().includes(searchQuery.toLowerCase());
      const subCatId = sub.category?._id || sub.category;
      const matchesCategory = categoryFilter === "all" || String(subCatId) === String(categoryFilter);
      return matchesSearch && matchesCategory;
    });
  }, [subCategoryData, searchQuery, categoryFilter]);

  const totalPages = Math.ceil(filteredSubCategories.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(startIndex + entriesPerPage, filteredSubCategories.length);

  const paginatedSubCategories = useMemo(() => {
    return filteredSubCategories.slice(startIndex, endIndex);
  }, [filteredSubCategories, startIndex, endIndex]);

  const handleDeleteClick = (subCategory) => {
    setSubCategoryToDelete(subCategory);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!subCategoryToDelete) return;
    try {
      setDeleteLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/sub-categories/delete-sub-category/${subCategoryToDelete._id}`
      );
      setIsDeleteModalOpen(false);
      setSubCategoryToDelete(null);
      await fetchSubCategories();
    } catch (err) {
      console.error("Error deleting sub-category:", err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingSubCategory(null);
    setIsSubAddModal(true);
  };

  const openEditModal = (subcategory) => {
    setEditingSubCategory(subcategory);
    setIsSubAddModal(true);
  };

  const openViewModal = (subcategory) => {
    setViewSubCategory(subcategory);
  };

  const renderCard = (sub, i) => {
    const subName = getEnglishName(sub);
    const subDescription = getEnglishDescription(sub);
    const categoryName = getEnglishName(sub.category);
    
    return (
      <div
        key={sub._id || i}
        className="grid grid-cols-2 h-[220px] bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200"
      >
        {/* Left Image */}
        <div className="w-full h-auto overflow-hidden bg-gray-100">
          <img
            src={sub.imgUrl || sub.category?.imgUrl || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&auto=format&fit=crop&q=60"}
            alt={subName}
            onError={(e) => {
              e.target.src = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&auto=format&fit=crop&q=60";
            }}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
          />
        </div>

        {/* Right Content */}
        <div className="flex-1 p-4 flex flex-col justify-between">
          <div>
            <h3 className="font-bold text-base text-gray-800 capitalize truncate">
              {subName}
            </h3>
            <p className="text-gray-500 text-xs line-clamp-2 mt-1.5">
              {subDescription}
            </p>
            {categoryName && (
              <span className="inline-block mt-3 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-full">
                {categoryName}
              </span>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => openViewModal(sub)}
              className="flex-1 flex justify-center items-center gap-1 bg-amber-50 text-[#ea8d2a] hover:bg-[#ea8d2a] hover:text-white py-2 rounded-lg font-medium transition"
              title="View Details"
            >
              {icons.eyeIcon}
            </button>
            <button
              onClick={() => openEditModal(sub)}
              className="flex-1 flex justify-center items-center gap-1 bg-blue-50 text-[#6278e8] hover:bg-[#5c73e6] hover:text-white py-2 rounded-lg font-medium transition"
              title="Edit"
            >
              {icons.editIcon}
            </button>
            <button
              onClick={() => handleDeleteClick(sub)}
              className="flex-1 flex justify-center items-center gap-1 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white py-2 rounded-lg font-medium transition"
              title="Delete"
            >
              {icons.deleteIcon}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <section className="min-h-screen">
        {!hideHeader && (
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
            <h1 className="text-xl md:text-3xl flex gap-1 items-center font-philosopher text-primaryTextColor">
              <span className="text-primaryColor">{icons.subCategoryIcon}</span> 
              Sub Categories
            </h1>
          </div>
        )}

        <div className="min-h-screen mt-2">
          <article className="flex flex-col bg-white rounded-lg p-4 sm:p-6 md:flex-row justify-between gap-4 md:items-center shadow-xs border border-gray-100">
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  placeholder="Search Subcategories..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                />
                <button className="text-xl hover:text-primaryColor transition-colors duration-500">
                  <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                </button>
              </div>

              {/* Dynamic Category Filter Dropdown */}
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1);
                  if (e.target.value === "all" && onClearCategoryFilter) {
                    onClearCategoryFilter();
                  }
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-full sm:w-56 font-medium text-gray-700"
              >
                <option value="all">All Categories ({subCategoryData.length})</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>
                    {getEnglishName(c)}
                  </option>
                ))}
              </select>
            </div>

            <AddOneButton onClick={openAddModal} label="Add new subcategory" />
          </article>

          <div className="">
            {loading ? <AdminSubCategorySkeleton /> : error ? (
              <div className="text-center text-red-500 py-10">{error}</div>
            ) : filteredSubCategories.length > 0 ? (
              <div className="my-6 min-h-[250px] grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
                {paginatedSubCategories.map(renderCard)}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="text-6xl text-gray-300 mb-4">
                  {icons.folderIcon}
                </div>
                <h2 className="text-2xl font-semibold text-gray-500 mb-2">
                  No Subcategories Found
                </h2>
                <p className="text-gray-400 mb-6">
                  Get started by creating a new subcategory
                </p>
                <AddOneButton
                  onClick={() => setIsSubAddModal(true)}
                  label="Add New Sub Category"
                />
              </div>
            )}

            {/* Pagination */}
            {!loading && filteredSubCategories.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredSubCategories.length)} of {filteredSubCategories.length} entries
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-4 py-2 rounded-lg transition ${currentPage === i + 1
                        ? 'bg-blue-500 text-white'
                        : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </section>

      {isSubAddModal &&
        createPortal(
          <AddSubCategoryModal
            isSubAddModal={isSubAddModal}
            onClose={() => setIsSubAddModal(false)}
            subCategoryData={editingSubCategory}
            onSuccess={fetchSubCategories}
          />,
          document.body
        )}

      {viewSubCategory &&
        createPortal(
          <SubCategoryDetailsModal
            isOpen={!!viewSubCategory}
            subCategory={viewSubCategory}
            onClose={() => setViewSubCategory(null)}
          />,
          document.body
        )}

      {isDeleteModalOpen &&
        createPortal(
          <DeleteAlertModal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            dltType="subcategory"
            dltName={getEnglishName(subCategoryToDelete)}
            onConfirm={handleConfirmDelete}
            dltLoading={deleteLoading}
          />,
          document.body
        )}
    </>
  );
};

export default AdminSubCategoryHome;